import { Toaster as Sonner, toast as sonnerToast } from "sonner";
import { useTheme } from "./theme/ThemeProvider";
import type { ComponentProps, ReactNode } from "react";
import type { ExternalToast } from "sonner";

type ToasterProps = ComponentProps<typeof Sonner>;

export const SONNER_TOAST_CLASSNAMES = {
  toast:
    "group toast group-[.toaster]:bg-background group-[.toaster]:text-foreground group-[.toaster]:border-border group-[.toaster]:shadow-lg",
  description: "group-[.toast]:text-muted-foreground",
  actionButton:
    "group-[.toast]:bg-primary group-[.toast]:text-primary-foreground",
  cancelButton:
    "group-[.toast]:bg-muted group-[.toast]:text-muted-foreground",
} as const;

/**
 * Toaster — theme-aware sonner toaster for @flowstate/ui consumers.
 *
 * Reads the resolved theme from the shared ThemeProvider context.
 * Requires a ThemeProvider ancestor.
 */
const Toaster = ({ ...props }: ToasterProps) => {
  const { theme } = useTheme();
  const resolvedTheme = theme === "system" ? "system" : theme;

  return (
    <Sonner
      theme={resolvedTheme as ToasterProps["theme"]}
      position="top-right"
      className="toaster group"
      closeButton
      toastOptions={{
        duration: 5000,
        classNames: SONNER_TOAST_CLASSNAMES,
      }}
      {...props}
    />
  );
};

export { Toaster };

/**
 * Wrapped toast with error-first UX conventions:
 * - toast.error / toast.warning persist until dismissed (duration = Infinity)
 * - all other variants inherit the Toaster's default 5s duration
 *
 * Import this `toast` instead of the raw sonner `toast` for consistent UX.
 */
const PERSISTENT: ExternalToast = { duration: Number.POSITIVE_INFINITY };

export const toast = Object.assign(
  (message: string | ReactNode, options?: ExternalToast) =>
    sonnerToast(message, options),
  {
    ...sonnerToast,
    error: (message: string | ReactNode, options?: ExternalToast) =>
      sonnerToast.error(message, { ...PERSISTENT, ...options }),
    warning: (message: string | ReactNode, options?: ExternalToast) =>
      sonnerToast.warning(message, { ...PERSISTENT, ...options }),
  },
);
